import React from "react";
import useStore from "../../../helpers/useStore";
import { observer } from "mobx-react";
import { RxLightDto } from "../../../api-client";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { useUnpaidBadgeCount } from "../../../stores/Transactions/transaction.queries";
import { RxListItem } from "./RxListItem";

interface RxSelectorProps {
  data: RxLightDto[];
  selectedRxId: string | undefined;
  handleRowClick: (rx: any) => void;
}

/**
 * The patient's prescriptions as a selectable list.
 *
 * One bordered container with flush rows and hairline separators — the
 * standard idiom for "pick one of these" (design system: "Dense lists:
 * bordered rows, not rounded-rect cards"). Each row used to be its own
 * floating card with gaps between rows, which read as a pile of widgets
 * rather than a list. The container owns the edge, radius and shadow; a row
 * owns only its Rx rail, a hover tint and the selection tint.
 *
 * `overflow-hidden` is what lets the rows stay square: the container's
 * radius clips row corners into the container's rounding.
 *
 * Separators are explicit per-row borders, NOT `divide-y divide-solid`. The
 * divide utilities are a preflight trap one worse than `border-solid`:
 * `divide-solid` sets border-style on all four sides of every row after the
 * first, `divide-y` overrides only the top width, and with preflight disabled
 * the other sides fall back to the UA default width (`medium`, 3px) — every
 * row except the first grew invisible 3px side borders and its content sat
 * 3px narrower than row one. `border-0` first, then the one side we mean.
 */
const RxSelector: React.FC<RxSelectorProps> = observer(
  ({ data, selectedRxId }) => {
    const PatientStore = useStore(PatientsContext);
    const { setSelectedRxId, selectedPatientId } = PatientStore;

    const { data: unpaidBadgeData } = useUnpaidBadgeCount(
      selectedPatientId || 0
    );

    return (
      <div
        role="list"
        aria-label="Rezepte"
        className="overflow-hidden rounded-card border border-solid border-ink-edge bg-card shadow-rest"
      >
        {data.map((rx) => {
          const isUnpaid = unpaidBadgeData?.rxs?.find(
            (unpaidRx) => unpaidRx.rxId === rx.id
          );
          return (
            <div
              role="listitem"
              key={rx.id}
              className="border-0 border-t border-solid border-ink-line-subtle first:border-t-0"
            >
              <RxListItem
                rx={rx}
                variant="row"
                isSelected={selectedRxId === rx.id}
                onClick={() => setSelectedRxId(rx.id)}
                unpaidBadgeData={
                  isUnpaid
                    ? {
                        dueAmount: isUnpaid.dueAmount,
                        tarifGroup: isUnpaid.tarifGroup,
                      }
                    : undefined
                }
              />
            </div>
          );
        })}
      </div>
    );
  }
);

export default RxSelector;
